// @flow
import japanPostalCode from 'japan-postal-code';
import debounce from 'lodash.debounce';
// FIXME: replace google-libphonenumber with a ligher lib
import { isMobileOnly } from 'react-device-detect';
import GooglePhoneNumber from 'google-libphonenumber';
import Encoding from 'encoding-japanese';

declare var document: {
  ...Document,
  msHidden: string,
  webkitHidden: string,
  addEventListener: Function,
};

export const normalizeMetadata = (metadata: any): Object => {
  const normalized = {};

  if (typeof metadata === 'object') {
    Object.keys(metadata).forEach(key => {
      normalized[key] = metadata[key].toString();
    });
  }

  return normalized;
};

/* eslint-disable */
export const flattenObject = ob => {
  let toReturn = {};

  for (let i in ob) {
    if (!ob.hasOwnProperty(i)) {
      continue;
    }

    if (typeof ob[i] == 'object') {
      let flatObject = flattenObject(ob[i]);

      for (let x in flatObject) {
        if (!flatObject.hasOwnProperty(x)) {
          continue;
        }

        toReturn[i + '.' + x] = flatObject[x];
      }
    } else {
      toReturn[i] = ob[i];
    }
  }

  return toReturn;
};
/* eslint-enable */

export const isFunction = (fn: Function): boolean =>
  fn && {}.toString.call(fn) === '[object Function]';

export const errorRoute = (err: Object, history: Object): void => {
  switch (err.status) {
    case 400: {
      switch (err.data.code) {
        case 'consumer.email.mismatch':
        case 'consumer.phone.mismatch': {
          const subRoute = err.data.code.split('.').join('-');

          history.push(`/error/${subRoute}`);
          break;
        }
        default:
          break;
      }

      break;
    }
    case 500: {
      break;
    }
    default:
      break;
  }
};

export const getVisibilityChangeProps = (): { hidden: ?string, visibilityChange: ?string } => {
  let hidden: ?string;
  let visibilityChange: ?string;

  if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  }

  return { hidden, visibilityChange };
};

// export const zenToHan = (zen: string) =>
//   zen.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0));

export const zenToHan = (zen: string) => Encoding.toHankanaCase(zen);

export const setValueByIndex = (_idx: number, value: string, arr: Array<string>): Array<string> => {
  const newArr: Array<string> = arr.slice();

  newArr[_idx] = zenToHan(value);

  return newArr;
};

export const getParsedPhoneNumber = (phone: ?string): string => {
  if (!phone) {
    return '';
  }

  const PNF = GooglePhoneNumber.PhoneNumberFormat;
  const phoneUtil = GooglePhoneNumber.PhoneNumberUtil.getInstance();

  // parse first
  try {
    const parsedRawPhoneNumber = phoneUtil.parseAndKeepRawInput(phone, 'JP');

    if (!phoneUtil.isValidNumber(parsedRawPhoneNumber)) {
      console.log('The phone format is incorrect, please try again.');

      return phone;
    }

    return phoneUtil.format(parsedRawPhoneNumber, PNF.E164).replace('+', '');
  } catch (error) {
    console.error(
      "Unable to parse the phone number with unexpected format, maybe it's too long? Using the original input phone number."
    );

    return phone;
  }
};

export const isValidPhoneNumber = (phone: ?string) => {
  if (!phone) {
    return false;
  }

  try {
    const phoneUtil = GooglePhoneNumber.PhoneNumberUtil.getInstance();
    const parsedRawPhoneNumber = phoneUtil.parseAndKeepRawInput(phone, 'JP');

    return phoneUtil.isValidNumber(parsedRawPhoneNumber);
  } catch (error) {
    console.error(
      "Unable to parse the phone number with unexpected format, maybe it's too long? Regarding it as invalid phone number."
    );

    return false;
  }
};

// toString is to make sure unexpected input never cause issue
export const getFormattedZip = (zip: string) => zip.toString().replace(/\D/g, '');

export const isLocalStorageSupported = (): boolean => {
  if (typeof isLocalStorageSupported.result !== 'undefined') {
    return isLocalStorageSupported.result;
  }

  const mod = 'modernizr';

  try {
    localStorage.setItem(mod, mod);
    localStorage.removeItem(mod);

    isLocalStorageSupported.result = true;
  } catch (error) {
    isLocalStorageSupported.result = false;
  }

  return isLocalStorageSupported.result;
};

export const getDesktopContainerStyle = (height: number): ?Object => {
  const maxHeight = window.innerHeight * 0.9;

  return isMobileOnly ? undefined : { height: Math.min(height, maxHeight - 136) };
};

const getPosition = element => {
  if (!element) {
    return null;
  }

  let xPosition = 0;
  let yPosition = 0;
  let _element = element;

  while (_element) {
    xPosition += _element.offsetLeft - _element.scrollLeft + _element.clientLeft;
    yPosition += _element.offsetTop - _element.scrollTop + _element.clientTop;
    _element = _element.offsetParent;
  }

  return { x: xPosition, y: yPosition };
};

export const scrollToFirstError = () => {
  setTimeout(() => {
    try {
      // $FlowFixMe
      const firstErrorEl = document.querySelector('.error');
      const pos = getPosition(firstErrorEl);

      if (pos !== null) {
        window.scrollTo(0, pos.y);
      }
    } catch (error) {
      // do nothing
    }
  }, 0);
};

const ZIP_REQ_DELAY = 250;

export const debouncedZipReq = debounce(({ updatePairs, value, name }) => {
  try {
    japanPostalCode.get(value, (address: Object) => {
      if (address) {
        if (name === 'regZip') {
          updatePairs({
            regState: address.prefecture,
            regCity: `${address.city}${address.area}`,
            /**
             * per So-san's request, we remove the following fields
             * because the users intend to change the address
             */
            regLine1: '',
            regLine2: '',
          });
        } else {
          updatePairs({
            state: address.prefecture,
            city: `${address.city}${address.area}`,
            /**
             * per So-san's request, we remove the following fields
             * because the users intend to change the address
             */
            line1: '',
            line2: '',
          });
        }
      }
    });
  } catch (error) {
    // Sentry.captureException(error);
  }
}, ZIP_REQ_DELAY);

/**
 * @param {?string} url The URL to check
 * @return Return the URL if its absolute or protocol relative URL
 *         Otherwise, return false.
 */
export const absoluteURL = (url: ?string): string => {
  if (typeof url === 'string' && /^(https?:\/\/|\/\/)/.test(url)) {
    return url;
  }

  return '';
};

export const isEmailVerifyResultValid = (res: ?Object) => {
  if (!res) return false;

  try {
    const data = res.data;

    if(!data) return false;
    
    return ['valid', 'catchall', 'unknown'].indexOf(data.result) >= 0;
  } catch (error) {
    // Handle JSON parsing errors explicitly
    console.error(error);

    return false;
  }
};

export default {
  isFunction,
  errorRoute,
  getVisibilityChangeProps,
  setValueByIndex,
  isValidPhoneNumber,
  getFormattedZip,
  isLocalStorageSupported,
  scrollToFirstError,
  debouncedZipReq,
  zenToHan,
};
